import {
    chartRefreshScheduler,
    computeStaleState,
    formatAge,
    FrameBudgetTracker,
    measureFrame,
    RingBuffer,
    getStaleThresholdMs,
    setSnapshot,
    getSnapshot,
    reportChartRenderMetric,
    getChartRenderTelemetrySnapshot,
} from '../../charts/engine';
import { renderDonutChart, renderHeatmapGrid, renderLineChart } from '../../charts/primitives';
import { apiFetch } from '../../services/api-client';
import { fetchBreadth, fetchIndices } from '../../services/market-data';
import { isFeatureEnabled } from '../../services/runtime-config';

type CacheTelemetryTotals = {
    hits: number;
    misses: number;
    errors: number;
    hitRatePct: number;
};

type CacheTelemetryResponse = {
    totals?: CacheTelemetryTotals;
    timestamp?: string;
};

type GlobalSnapshot = {
    hitRateSeries: number[];
    adRatioSeries: number[];
    cadenceDriftSeries: number[];
    errorRateSeries: number[];
    freshnessLagSeries: number[];
    cacheEfficiencySeries: number[];
    adDriftSeries: number[];
    throughputSeries: number[];
    indexSpreadSeries: number[];
    latestTelemetry: CacheTelemetryTotals | null;
    lastUpdatedAt: number;
};

const teardownMap = new WeakMap<HTMLElement, () => void>();

export function renderPhase2GlobalTelemetryEngine(container: HTMLElement): void {
    if (!isFeatureEnabled('charts_v2_engine')) {
        container.innerHTML = '';
        return;
    }

    teardownMap.get(container)?.();

    container.innerHTML = `
      <section class="terminal-global-telemetry" id="terminal-global-telemetry-card">
        <div class="terminal-global-telemetry__slot">
                    <div class="terminal-global-telemetry__title v2-chart-title">Cache Hit Rate</div>
          <canvas id="terminal-global-hitrate" class="terminal-global-telemetry__canvas"></canvas>
        </div>

        <div class="terminal-global-telemetry__slot">
                    <div class="terminal-global-telemetry__title v2-chart-title">A/D Ratio Pulse</div>
          <canvas id="terminal-global-adratio" class="terminal-global-telemetry__canvas"></canvas>
        </div>

        <div class="terminal-global-telemetry__slot">
                    <div class="terminal-global-telemetry__title v2-chart-title">Cache Split</div>
          <canvas id="terminal-global-cache-split" class="terminal-global-telemetry__canvas"></canvas>
        </div>

        <div class="terminal-global-telemetry__slot">
                    <div class="terminal-global-telemetry__title v2-chart-title">Index Heat Grid</div>
          <canvas id="terminal-global-index-grid" class="terminal-global-telemetry__canvas"></canvas>
        </div>

                <div class="terminal-global-telemetry__slot">
                    <div class="terminal-global-telemetry__title v2-chart-title">Render Pressure Grid</div>
                    <canvas id="terminal-global-render-grid" class="terminal-global-telemetry__canvas"></canvas>
                </div>

                <div class="terminal-global-telemetry__slot">
                    <div class="terminal-global-telemetry__title v2-chart-title">Cadence Drift</div>
                    <canvas id="terminal-global-cadence" class="terminal-global-telemetry__canvas"></canvas>
                </div>

                <div class="terminal-global-telemetry__slot">
                    <div class="terminal-global-telemetry__title v2-chart-title">Cache Error Rate</div>
                    <canvas id="terminal-global-error-rate" class="terminal-global-telemetry__canvas"></canvas>
                </div>

                <div class="terminal-global-telemetry__slot">
                    <div class="terminal-global-telemetry__title v2-chart-title">Feed Freshness Lag</div>
                    <canvas id="terminal-global-freshness-lag" class="terminal-global-telemetry__canvas"></canvas>
                </div>

                <div class="terminal-global-telemetry__slot">
                    <div class="terminal-global-telemetry__title v2-chart-title">Cache Efficiency Pulse</div>
                    <canvas id="terminal-global-cache-efficiency" class="terminal-global-telemetry__canvas"></canvas>
                </div>

                <div class="terminal-global-telemetry__slot">
                    <div class="terminal-global-telemetry__title v2-chart-title">A/D Drift Pulse</div>
                    <canvas id="terminal-global-ad-drift" class="terminal-global-telemetry__canvas"></canvas>
                </div>

                <div class="terminal-global-telemetry__slot">
                    <div class="terminal-global-telemetry__title v2-chart-title">Cache Throughput Pulse</div>
                    <canvas id="terminal-global-throughput" class="terminal-global-telemetry__canvas"></canvas>
                </div>

                <div class="terminal-global-telemetry__slot">
                    <div class="terminal-global-telemetry__title v2-chart-title">Index Change Spread</div>
                    <canvas id="terminal-global-index-spread" class="terminal-global-telemetry__canvas"></canvas>
                </div>

        <div class="terminal-global-telemetry__board" id="terminal-global-board"></div>
        <div class="terminal-global-telemetry__meta" id="terminal-global-meta">Booting global telemetry...</div>
        <div class="terminal-global-telemetry__stale" id="terminal-global-stale" style="display:none;">Global telemetry stale. Showing cached state.</div>
      </section>
    `;

    const hitRateCanvas = container.querySelector('#terminal-global-hitrate') as HTMLCanvasElement | null;
    const adRatioCanvas = container.querySelector('#terminal-global-adratio') as HTMLCanvasElement | null;
    const cacheDonut = container.querySelector('#terminal-global-cache-split') as HTMLCanvasElement | null;
    const heatGridCanvas = container.querySelector('#terminal-global-index-grid') as HTMLCanvasElement | null;
    const renderGridCanvas = container.querySelector('#terminal-global-render-grid') as HTMLCanvasElement | null;
    const cadenceCanvas = container.querySelector('#terminal-global-cadence') as HTMLCanvasElement | null;
    const errorRateCanvas = container.querySelector('#terminal-global-error-rate') as HTMLCanvasElement | null;
    const freshnessLagCanvas = container.querySelector('#terminal-global-freshness-lag') as HTMLCanvasElement | null;
    const cacheEfficiencyCanvas = container.querySelector('#terminal-global-cache-efficiency') as HTMLCanvasElement | null;
    const adDriftCanvas = container.querySelector('#terminal-global-ad-drift') as HTMLCanvasElement | null;
    const throughputCanvas = container.querySelector('#terminal-global-throughput') as HTMLCanvasElement | null;
    const indexSpreadCanvas = container.querySelector('#terminal-global-index-spread') as HTMLCanvasElement | null;
    const board = container.querySelector('#terminal-global-board') as HTMLElement | null;
    const meta = container.querySelector('#terminal-global-meta') as HTMLElement | null;
    const staleNode = container.querySelector('#terminal-global-stale') as HTMLElement | null;

    if (!hitRateCanvas || !adRatioCanvas || !cacheDonut || !heatGridCanvas || !renderGridCanvas || !cadenceCanvas || !errorRateCanvas || !freshnessLagCanvas || !cacheEfficiencyCanvas || !adDriftCanvas || !throughputCanvas || !indexSpreadCanvas || !board || !meta || !staleNode) return;

    const snapshotKey = 'phase2:global-telemetry';
    const staleThresholdMs = getStaleThresholdMs('B');
    const frameTracker = new FrameBudgetTracker(120, 16);
    const hitRateSeries = new RingBuffer<number>(180);
    const adRatioSeries = new RingBuffer<number>(180);
    const cadenceDriftSeries = new RingBuffer<number>(180);
    const errorRateSeries = new RingBuffer<number>(180);
    const freshnessLagSeries = new RingBuffer<number>(180);
    const cacheEfficiencySeries = new RingBuffer<number>(180);
    const adDriftSeries = new RingBuffer<number>(180);
    const throughputSeries = new RingBuffer<number>(180);
    const indexSpreadSeries = new RingBuffer<number>(180);

    let lastUpdatedAt: number | null = null;
    let latestTotals: CacheTelemetryTotals | null = null;
    let previousTickAt: number | null = null;

    const draw = async (indexChanges: number[]) => {
        const sample = measureFrame('global-telemetry-engine', () => {
            renderLineChart(hitRateCanvas, hitRateSeries.toArray(), '#15b57a');
            renderLineChart(adRatioCanvas, adRatioSeries.toArray(), '#5aa6ff');

            const totals = latestTotals || { hits: 0, misses: 0, errors: 0, hitRatePct: 0 };
            renderDonutChart(cacheDonut, [totals.hits, totals.misses, totals.errors], ['#15b57a', '#ff9f43', '#d94736']);

            const cols = 6;
            const rows = 2;
            const cells = indexChanges.slice(0, cols * rows).map((value, idx) => ({
                x: idx % cols,
                y: Math.floor(idx / cols),
                value,
            }));
            renderHeatmapGrid(heatGridCanvas, cells, cols, rows);

            renderLineChart(cadenceCanvas, cadenceDriftSeries.toArray(), '#ff9f43');
            renderLineChart(errorRateCanvas, errorRateSeries.toArray(), '#d94736');
            renderLineChart(freshnessLagCanvas, freshnessLagSeries.toArray(), '#7cc4fa');
            renderLineChart(cacheEfficiencyCanvas, cacheEfficiencySeries.toArray(), '#15b57a');
            renderLineChart(adDriftCanvas, adDriftSeries.toArray(), '#c67bff');
            renderLineChart(throughputCanvas, throughputSeries.toArray(), '#ff9f43');
            renderLineChart(indexSpreadCanvas, indexSpreadSeries.toArray(), '#5aa6ff');

            const telemetry = getChartRenderTelemetrySnapshot();
            const pressureCells = telemetry.items.slice(0, cols * rows).map((item, idx) => {
                const ratio = item.budgetMs > 0 ? (item.latestMs / item.budgetMs) : 0;
                const normalized = Math.max(-5, Math.min(5, (ratio - 1) * 5));
                return {
                    x: idx % cols,
                    y: Math.floor(idx / cols),
                    value: normalized,
                };
            });
            renderHeatmapGrid(renderGridCanvas, pressureCells, cols, rows);
        });

        frameTracker.push(sample.label, sample.durationMs);
        const avg = frameTracker.getAverageMs();
        const within = frameTracker.isWithinBudget(sample.durationMs);
        reportChartRenderMetric({
            id: 'phase2-global-telemetry',
            label: 'Global Telemetry',
            latestMs: sample.durationMs,
            averageMs: avg,
            budgetMs: frameTracker.getBudgetMs(),
            withinBudget: within,
        });

        const chartTelemetry = getChartRenderTelemetrySnapshot();
        const budgetState = chartTelemetry.count > 0
            ? `${chartTelemetry.overBudget}/${chartTelemetry.count} over · avg ${chartTelemetry.averageLatestMs.toFixed(2)}ms`
            : 'no chart samples';
        const hottest = chartTelemetry.items
            .filter(item => item.budgetMs > 0)
            .sort((a, b) => (b.latestMs / b.budgetMs) - (a.latestMs / a.budgetMs))[0] || null;
        const hottestRatio = hottest && hottest.budgetMs > 0 ? (hottest.latestMs / hottest.budgetMs) : 0;

        const stale = computeStaleState(lastUpdatedAt, staleThresholdMs);
        staleNode.style.display = stale.stale ? 'flex' : 'none';

        board.innerHTML = `
          <div class="terminal-list__row"><span>Cache Hit Rate</span><strong>${(latestTotals?.hitRatePct || 0).toFixed(1)}%</strong></div>
          <div class="terminal-list__row"><span>Cache Hits/Misses/Errors</span><strong>${latestTotals?.hits || 0}/${latestTotals?.misses || 0}/${latestTotals?.errors || 0}</strong></div>
          <div class="terminal-list__row"><span>Chart Budget</span><strong>${budgetState}</strong></div>
                    <div class="terminal-list__row"><span>Hot Render Path</span><strong>${hottest ? `${hottest.label} ${hottestRatio.toFixed(2)}x` : 'n/a'}</strong></div>
          <div class="terminal-list__row"><span>Frame Cost</span><strong>${sample.durationMs.toFixed(2)}ms</strong></div>
        `;

        meta.textContent = stale.stale
            ? `Stale: ${formatAge(stale.ageMs)} old`
            : `Global telemetry live ${new Date(lastUpdatedAt || Date.now()).toLocaleTimeString('en-IN')}`;
    };

    const persist = () => {
        setSnapshot<GlobalSnapshot>(snapshotKey, {
            hitRateSeries: hitRateSeries.toArray(),
            adRatioSeries: adRatioSeries.toArray(),
            cadenceDriftSeries: cadenceDriftSeries.toArray(),
            errorRateSeries: errorRateSeries.toArray(),
            freshnessLagSeries: freshnessLagSeries.toArray(),
            cacheEfficiencySeries: cacheEfficiencySeries.toArray(),
            adDriftSeries: adDriftSeries.toArray(),
            throughputSeries: throughputSeries.toArray(),
            indexSpreadSeries: indexSpreadSeries.toArray(),
            latestTelemetry: latestTotals,
            lastUpdatedAt: lastUpdatedAt || Date.now(),
        });
    };

    const update = async () => {
        try {
            const [telemetry, breadth, indices] = await Promise.all([
                apiFetch<CacheTelemetryResponse>('/cache-telemetry', { cacheTTL: 15, staleWhileRevalidate: false }),
                fetchBreadth(),
                fetchIndices(),
            ]);

            const totals = telemetry.data.totals;
            if (totals) {
                latestTotals = totals;
                hitRateSeries.push(totals.hitRatePct);
                const denom = totals.hits + totals.misses + totals.errors;
                const errorRate = denom > 0 ? (totals.errors / denom) * 100 : 0;
                errorRateSeries.push(errorRate);
                cacheEfficiencySeries.push(totals.hitRatePct - errorRate);
                throughputSeries.push(denom);
            }

            if (breadth) {
                const prevAd = adRatioSeries.toArray().slice(-1)[0] ?? breadth.adRatio;
                adRatioSeries.push(breadth.adRatio);
                adDriftSeries.push(breadth.adRatio - prevAd);
            }

            const now = Date.now();
            if (previousTickAt) {
                const expected = 5000;
                const driftMs = now - previousTickAt - expected;
                cadenceDriftSeries.push(Math.max(-5, Math.min(5, driftMs / 1000)));
            } else {
                cadenceDriftSeries.push(0);
            }
            previousTickAt = now;

            const lagMs = lastUpdatedAt ? Math.max(0, now - lastUpdatedAt) : 0;
            freshnessLagSeries.push(Math.min(120, lagMs / 1000));

            const indexChanges = indices.map(i => i.pChange);
            if (indexChanges.length > 0) {
                const maxChange = Math.max(...indexChanges);
                const minChange = Math.min(...indexChanges);
                indexSpreadSeries.push(maxChange - minChange);
            }

            lastUpdatedAt = Date.now();
            persist();
            await draw(indexChanges);
        } catch {
            if (lastUpdatedAt) {
                await draw([]);
            }
        }
    };

    const persisted = getSnapshot<GlobalSnapshot>(snapshotKey);
    if (persisted) {
        persisted.hitRateSeries.forEach(v => hitRateSeries.push(v));
        persisted.adRatioSeries.forEach(v => adRatioSeries.push(v));
        persisted.cadenceDriftSeries?.forEach(v => cadenceDriftSeries.push(v));
        persisted.errorRateSeries?.forEach(v => errorRateSeries.push(v));
        persisted.freshnessLagSeries?.forEach(v => freshnessLagSeries.push(v));
        persisted.cacheEfficiencySeries?.forEach(v => cacheEfficiencySeries.push(v));
        persisted.adDriftSeries?.forEach(v => adDriftSeries.push(v));
        persisted.throughputSeries?.forEach(v => throughputSeries.push(v));
        persisted.indexSpreadSeries?.forEach(v => indexSpreadSeries.push(v));
        latestTotals = persisted.latestTelemetry;
        lastUpdatedAt = persisted.lastUpdatedAt;
        previousTickAt = persisted.lastUpdatedAt;
        void draw([]);
    }

    const taskId = `phase2-global-telemetry-${Math.random().toString(36).slice(2)}`;
    const unregister = chartRefreshScheduler.register(taskId, 'B', () => {
        void update();
    }, 5000);

    teardownMap.set(container, () => {
        unregister();
    });
}
